/**
 * Integrytas Group - Common Security Module (CSM)
 * Command Registry — singleton that powers the Omnibox.
 *
 * Three result scopes:
 *   navigation  — pages from csm_page_master the user may access + open tabs
 *   action      — quick triggers registered by code (open screen, clear cache …)
 *   data        — entity search configs; each performs a live DB query
 *
 * The shell calls setShellContext() after auth so navigation / tab commands
 * are always current. Modules import this module at load time and call
 * registerAction() or registerSearch() once — no React lifecycle needed.
 *
 * Built-in entity searches: Users, Roles, Pages (CSM tables).
 * Downstream modules register their own (e.g. Vendors, Invoices) via
 * registerSearch().
 */

import { apiDb } from './api_client';
import type { AuthSession, PageAccess } from '../types/auth';
import { is_nav_group_code } from './nav_groups';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OpenTab {
  page_internal_code: string;
  display_text:       string;
  route_path:         string;
}

export type CommandScope = 'navigation' | 'action' | 'data';

export interface Command {
  id:          string;
  scope:       CommandScope;
  label:       string;
  description?: string;
  keywords?:   string[];
  onSelect:    () => void;
}

export interface EntitySearchConfig {
  type:   string;   // e.g. 'user'
  label:  string;   // e.g. 'Users'
  icon?:  string;   // emoji or lucide icon name
  /** Must return ≤ 10 results quickly — debounced by the omnibox. */
  search: (query: string) => Promise<EntityResult[]>;
}

export interface EntityResult {
  id:          string | number;
  label:       string;
  description?: string;
  entityType:  string;
  onSelect:    () => void;
}

// ---------------------------------------------------------------------------
// Shell context (set by TabManagerShell on every render cycle)
// ---------------------------------------------------------------------------

interface ShellContext {
  menu:        PageAccess[];
  tabs:        OpenTab[];
  openTab:     (tab: OpenTab) => void;
  session:     AuthSession;
  pageLabels:  Record<string, string>;
}

// ---------------------------------------------------------------------------
// Singleton implementation
// ---------------------------------------------------------------------------

class CommandRegistry {
  private shellCtx: ShellContext | null = null;
  private actions = new Map<string, Command>();
  private searches = new Map<string, EntitySearchConfig>();

  /** Called by TabManagerShell whenever its context changes. */
  setShellContext(ctx: ShellContext) {
    this.shellCtx = ctx;
    this.ensureBuiltInSearches();
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Register a named quick action.  Safe to call at module load time. */
  registerAction(cmd: Command) {
    this.actions.set(cmd.id, cmd);
  }

  unregisterAction(id: string) {
    this.actions.delete(id);
  }

  // ── Entity searches ───────────────────────────────────────────────────────

  registerSearch(config: EntitySearchConfig) {
    this.searches.set(config.type, config);
  }

  unregisterSearch(type: string) {
    this.searches.delete(type);
  }

  // ── Query API (called by the Omnibox on every keystroke) ──────────────────

  queryNavigation(q: string): Command[] {
    if (!this.shellCtx) return [];
    const lq = q.toLowerCase();
    const { menu, tabs, openTab, pageLabels } = this.shellCtx;

    const results: Command[] = [];

    // Pages the user can view — skip NAV.* group nodes (no component registered)
    const allowed = menu.filter(p => p.can_view && !is_nav_group_code(p.page_internal_code));
    for (const p of allowed) {
      const label = pageLabels[p.page_internal_code] ?? p.page_internal_code;
      if (
        !label.toLowerCase().includes(lq) &&
        !p.page_internal_code.toLowerCase().includes(lq) &&
        !p.route_path.toLowerCase().includes(lq)
      ) continue;
      results.push({
        id:       `nav:${p.page_internal_code}`,
        scope:    'navigation',
        label,
        description: p.route_path,
        onSelect: () => openTab({
          page_internal_code: p.page_internal_code,
          display_text:       label,
          route_path:         p.route_path,
        }),
      });
    }

    // Currently open tabs — "switch to"
    for (const tab of tabs) {
      if (!tab.display_text.toLowerCase().includes(lq) &&
          !tab.page_internal_code.toLowerCase().includes(lq)) continue;
      results.push({
        id:          `tab:${tab.page_internal_code}`,
        scope:       'navigation',
        label:       tab.display_text,
        description: 'Switch to open tab',
        keywords:    ['tab', 'switch', 'open'],
        onSelect:    () => openTab(tab),
      });
    }

    return results.slice(0, 12);
  }

  queryActions(q: string): Command[] {
    if (!q.trim()) return Array.from(this.actions.values()).slice(0, 8);
    const lq = q.toLowerCase();
    return Array.from(this.actions.values())
      .filter(a =>
        a.label.toLowerCase().includes(lq) ||
        a.description?.toLowerCase().includes(lq) ||
        a.keywords?.some(k => k.toLowerCase().includes(lq))
      )
      .slice(0, 8);
  }

  async queryEntities(q: string): Promise<EntityResult[]> {
    if (!q.trim() || q.length < 2) return [];
    const all = await Promise.all(
      Array.from(this.searches.values()).map(s =>
        s.search(q).catch(() => [] as EntityResult[])
      )
    );
    return all.flat().slice(0, 15);
  }

  // ── Built-in entity searches (CSM core) ──────────────────────────────────

  private builtInsRegistered = false;

  private ensureBuiltInSearches() {
    if (this.builtInsRegistered) return;
    this.builtInsRegistered = true;

    const ctx = (): ShellContext => {
      if (!this.shellCtx) throw new Error('CommandRegistry: shell context not set');
      return this.shellCtx;
    };

    // Users
    this.registerSearch({
      type:  'user',
      label: 'Users',
      icon:  '👤',
      search: async (q) => {
        const { data } = await apiDb
          .from('csm_user_master')
          .select('user_id,display_name,email')
          .or(`display_name.ilike.%${q}%,email.ilike.%${q}%`)
          .eq('is_active', 'Y')
          .limit(5);
        type UserRow = { user_id: string | number; display_name: string; email: string };
        return ((data ?? []) as UserRow[]).map((r) => ({
          id:          r.user_id,
          label:       r.display_name,
          description: r.email,
          entityType:  'user',
          onSelect: () => ctx().openTab({
            page_internal_code: 'CSM.USER.LIST',
            display_text:       `User: ${r.display_name}`,
            route_path:         '/users',
          }),
        }));
      },
    });

    // Roles
    this.registerSearch({
      type:  'role',
      label: 'Roles',
      icon:  '🛡',
      search: async (q) => {
        const { data } = await apiDb
          .from('csm_role_master')
          .select('role_id,internal_code,description')
          .or(`internal_code.ilike.%${q}%,description.ilike.%${q}%`)
          .eq('is_active', 'Y')
          .limit(5);
        type RoleRow = { role_id: string | number; internal_code: string; description: string | null };
        return ((data ?? []) as RoleRow[]).map((r) => ({
          id:          r.role_id,
          label:       r.internal_code,
          description: r.description ?? '',
          entityType:  'role',
          onSelect: () => ctx().openTab({
            page_internal_code: 'CSM.ROLE.LIST',
            display_text:       `Role: ${r.internal_code}`,
            route_path:         '/roles',
          }),
        }));
      },
    });

    // Pages
    this.registerSearch({
      type:  'page',
      label: 'Pages',
      icon:  '📄',
      search: async (q) => {
        // Navigation is Base-Application-only. csm_page_master also holds other
        // applications' non-navigable catalog rows, so scope the palette to the
        // default application (is_default='Y'); NULL app = default/legacy. The
        // embedded join is a LEFT join, so NULL-app rows survive the JS filter.
        const { data } = await apiDb
          .from('csm_page_master')
          .select('page_id,internal_code,route_path,application_id, csm_application_master:application_id ( is_default )')
          .or(`internal_code.ilike.%${q}%,route_path.ilike.%${q}%`)
          .eq('is_active', 'Y')
          .limit(20);
        type PageRow = {
          page_id: string | number; internal_code: string; route_path: string;
          application_id: number | null; csm_application_master: { is_default: 'Y' | 'N' } | null;
        };
        return ((data ?? []) as PageRow[])
          .filter((r) => r.application_id == null || r.csm_application_master?.is_default === 'Y')
          .slice(0, 5)
          .map((r) => ({
          id:          r.page_id,
          label:       r.internal_code,
          description: r.route_path,
          entityType:  'page',
          onSelect: () => ctx().openTab({
            page_internal_code: r.internal_code,
            display_text:       r.internal_code,
            route_path:         r.route_path,
          }),
        }));
      },
    });
  }
}

export const commandRegistry = new CommandRegistry();
